import { HDate } from '@hebcal/core';

/**
 * Compute mourning period flags — Bein HaMetzarim (Three Weeks / Nine Days).
 *
 * The period of mourning between 17 Tammuz and 9 Av, commemorating the
 * destruction of the Beit HaMikdash. Mourning intensifies during the
 * Nine Days (1-9 Av) and the week in which Tisha B'Av falls.
 *
 * When 17 Tammuz or 9 Av falls on Shabbat and is postponed to Sunday,
 * the mourning period adjusts accordingly.
 *
 * @param {HDate} hdate - the halachic Hebrew date
 * @returns {object} mourning period data
 */
export function computeMourningPeriods(hdate) {
  const m = hdate.getMonth();
  const d = hdate.getDate();
  const dayOfWeek = hdate.getDay(); // 0=Sun, 6=Sat

  // Only relevant in Tammuz (month 4) and Av (month 5)
  if (m !== 4 && m !== 5) {
    return {
      isBeinHametzarim: false,
      isNineDays: false,
      isShavuaBeShechalBo: false,
      note: null,
    };
  }

  // ═══ 17 Tammuz — start of Three Weeks ═══
  // If 17 Tammuz falls on Shabbat, the fast is postponed to Sunday (18 Tammuz)
  // but mourning customs begin from 17 Tammuz itself
  const seventeenTammuz = new HDate(17, 4, hdate.getFullYear());
  const isTammuzNidcheh = seventeenTammuz.getDay() === 6; // Falls on Shabbat

  // ═══ 9 Av — end of Three Weeks ═══
  const nineAv = new HDate(9, 5, hdate.getFullYear());
  const isAvNidcheh = nineAv.getDay() === 6; // Falls on Shabbat → postponed to 10 Av
  const tishaBeAvDate = isAvNidcheh ? 10 : 9; // Actual fast day

  // ═══ Bein HaMetzarim (Three Weeks) ═══
  // From 17 Tammuz through 9 Av (or 10 Av if nidcheh)
  let isBeinHametzarim = false;
  if (m === 4 && d >= 17) {
    isBeinHametzarim = true;
  } else if (m === 5 && d <= tishaBeAvDate) {
    isBeinHametzarim = true;
  }

  // ═══ Nine Days (1-9 Av) ═══
  // From Rosh Chodesh Av through 9 Av (or 10 Av if nidcheh)
  const isNineDays = m === 5 && d >= 1 && d <= tishaBeAvDate;

  // ═══ Shavua SheChal Bo (Week of Tisha B'Av) ═══
  // The week in which Tisha B'Av falls — stricter mourning.
  // Determined by the Sunday before 9 Av through the fast day.
  // If 9 Av is on Sunday (nidcheh from Shabbat), some authorities are lenient
  // regarding the preceding week.
  let isShavuaBeShechalBo = false;
  if (isNineDays) {
    const tishaBeAvHDate = new HDate(tishaBeAvDate, 5, hdate.getFullYear());
    const tishaBeAvDow = tishaBeAvHDate.getDay();

    // Find the Sunday that starts the week of Tisha B'Av
    const sundayBefore = tishaBeAvHDate.abs() - tishaBeAvDow;
    const hdateAbs = hdate.abs();

    if (hdateAbs >= sundayBefore && hdateAbs <= tishaBeAvHDate.abs()) {
      isShavuaBeShechalBo = true;
    }
  }

  // ═══ Note ═══
  let note = null;
  if (isBeinHametzarim) {
    if (isShavuaBeShechalBo) {
      note = 'שבוע שחל בו תשעה באב — ימי אבלות מוגברים';
    } else if (isNineDays) {
      note = 'תשעת הימים — ימי אבלות';
    } else {
      note = 'בין המצרים — שלושת השבועות';
    }

    if (isTammuzNidcheh && m === 4 && d === 17) {
      note += ' (י״ז תמוז נדחה ליום ראשון)';
    }
    if (isAvNidcheh && m === 5 && d === 9) {
      note += ' (תשעה באב נדחה ליום ראשון)';
    }
  }

  return {
    isBeinHametzarim,
    isNineDays,
    isShavuaBeShechalBo,
    note,
  };
}
